import React from "react";
import BAGroupTimeFormat from "../../../../../utils/formatters/BAGroupTimeFormat";
import {
  SCOPE_OCCURRENCE,
  SCOPE_FOLLOWING,
  SCOPE_SERIES,
} from "../../../../../utils/calendarPersonalEvents";
import BdrModal from "../../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../../components/BdrButton/BdrButton";
import "./repeatScopeModal.css";

/**
 * "Which events does this apply to?" — shown before an edit or a delete on an
 * occurrence of a repeating personal event.
 * Plan: BDR_Mobile plans/impl_recurring_personal_events_2026_09_19.md §6.7.
 *
 * `mode="edit"` offers two choices; `mode="delete"` offers three. Editing a
 * single occurrence is deliberately NOT offered — to change one, delete it and
 * create a one-off, which keeps the series document the only thing that
 * defines its own occurrences.
 *
 * The server refuses an occurrence edit and defaults a scope-less occurrence
 * DELETE to just that occurrence, so this dialog is how the user expresses
 * intent, not the thing that keeps them safe.
 */
export default function RepeatScopeModal({
  open,
  mode = "edit",
  // The day the user SEES (the occurrence's startMs), never occurrenceDayMs.
  // Same today; once a single occurrence can be moved (phase B) the id keeps
  // the pattern slot while the event renders elsewhere, and "Delete only
  // Jan 20" for an event sitting on Jan 8 would be wrong.
  displayDayMs,
  splitAtMs,
  // Set on the EARLIER half of a split (2026-09-25, phase C2) — "all events"
  // stops there too. Before this only the later half knew it had been cut.
  truncatedAtMs,
  // How many occurrences carry their own details (phase B). An occurrence's
  // own value wins over a series edit on that field, and the hint says so.
  overrideCount = 0,
  warning,
  busy,
  onCancel,
  onChoose,
}) {
  const day = displayDayMs ? BAGroupTimeFormat(displayDayMs, true, true) : null;
  const keepLine =
    overrideCount > 0
      ? ` ${overrideCount} event${overrideCount === 1 ? "" : "s"} with ${overrideCount === 1 ? "its" : "their"} own details will keep them.`
      : "";


  const choices =
    mode === "delete"
      ? [
          {
            scope: SCOPE_OCCURRENCE,
            tone: "neutral",
            title: day ? `Delete only ${day}` : "Delete only this event",
            hint: "The rest of the series is untouched.",
          },
          {
            scope: SCOPE_FOLLOWING,
            tone: "warn",
            title: "Delete this and all future events",
            hint: "Earlier events stay on the calendar.",
          },
          {
            scope: SCOPE_SERIES,
            tone: "danger",
            title: "Delete all events in this series",
            hint: "Every occurrence, past and future. This cannot be undone.",
          },
        ]
      : [
          // Edit order (2026-09-25): narrowest first, the REVERSIBLE one
          // second, the permanent split last. "This and all future" forks the
          // document permanently and there is no merge; it used to sit first
          // with the gentlest wording, which is how a series ends up as three
          // documents after three note edits.
          {
            scope: SCOPE_OCCURRENCE,
            tone: "neutral",
            title: "Only this event",
            hint: "The rest of the series is untouched.",
          },
          {
            scope: SCOPE_SERIES,
            tone: "warn",
            title: "All events in this series",
            hint: `Including ones that have already passed.${keepLine}`,
          },
          {
            /* Merge 2026-09-28: this arrived from main still carrying `bg`,
               which nothing renders any more — the row reads `c.tone`, so it
               would have painted as rsm-choice--undefined. `danger` because
               main's own hint says it cannot be undone. */
            scope: SCOPE_FOLLOWING,
            tone: "danger",
            title: "This and all future events",
            hint: "Earlier events keep their current details. Permanently splits the series into two — can't be undone.",
          },
        ];

  return (
    <BdrModal
      open={!!open}
      onClose={busy ? () => {} : onCancel}
      closeOnBackdrop={!busy}
      closeOnEsc={!busy}
      title={mode === "delete" ? "Delete a repeating event" : "Edit a repeating event"}
      variant="panel"
      size="sm"
      /* peak: hosted by EditEventModal and the detail popup, matching the
         pay-period confirm that shares those hosts. */
      layer="peak"
      actions={
        <BdrButton disabled={busy} onClick={onCancel}>
          Cancel
        </BdrButton>
      }
    >
      <p className="rsm-lead">
        This event repeats. Which events should this apply to?
      </p>

      {/* A split fragment is its own series, so "all events" stops at the
          split. Saying so is cheaper than surprising someone later. */}
      {splitAtMs && (
        <p className="rsm-note">
          This series was split on <b>{BAGroupTimeFormat(splitAtMs, true, true)}</b>;
          events before that belong to a separate series.
        </p>
      )}

      {/* The other half of the same fact (2026-09-25, phase C2): this is
          the EARLIER piece, and a later series was split off from it.
          Merge 2026-09-28: main's `splitAtMs` twin of this is NOT repeated
          here — it is already above, converted to .rsm-note. */}
      {truncatedAtMs && (
        <p className="rsm-note">
          Events from <b>{BAGroupTimeFormat(truncatedAtMs, true, true)}</b> onward
          belong to a separate series.
        </p>
      )}

      {warning && (
        <p className="bdr-alert bdr-alert--warning bdr-alert--start">{warning}</p>
      )}

      <div className="rsm-choices">
        {choices.map((c) => (
          <button
            key={c.scope}
            type="button"
            className={`rsm-choice rsm-choice--${c.tone}`}
            disabled={busy}
            onClick={() => onChoose(c.scope)}
          >
            {c.title}
            <span className="rsm-choice-hint">{c.hint}</span>
          </button>
        ))}
      </div>
    </BdrModal>
  );
}
